import * as React from 'react';

import { 
    Box, 
    Typography, 
    CircularProgress,
    TextField,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Button,
    Alert
} from '@mui/material';

import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import SearchIcon from '@mui/icons-material/Search';

import { TokenValidator, STYLES } from '../../../../utils';
import { Orders } from '../../../../controllers';
import { Detail } from './detail';
import { Csv } from './internal';

export default function Main({handleChange}){

    const [token, setToken] = React.useState(undefined);
    const [records, setRecords] = React.useState([]);
    const display = records;
    const [filters, setFilters] = React.useState({id:'',type:'',client:'',date:'',reference:'',tag:'',status:'',maxStatus:'',minStatus:''});
    const [pages, setPages] = React.useState({total:1,actual:1});
    const [csvUi, showCsvUi] = React.useState(false);
    const [loading, isLoading] = React.useState(true);
    const filtering = loading;
    const [error, setError] = React.useState('');
    const [retry, setRetry] = React.useState(0);
    const [quickId, setQuickId] = React.useState('');
    const [quickSearching, setQuickSearching] = React.useState(false);
    const [quickNotice, setQuickNotice] = React.useState(null);
    const [quickMatches, setQuickMatches] = React.useState([]);
    React.useEffect(() => {
        let alive = true;
        TokenValidator.GetInternalUser().then(value => {
            if(alive) setToken(value);
        }).catch(() => { if(alive){setError('No se pudo validar la sesión.');isLoading(false);} });
        return () => {alive=false};
    }, []);
    React.useEffect(() => {
        if(token === undefined) return;
        let alive = true;
        isLoading(true);setError('');
        const timer = setTimeout(async() => {
            try {
                const response = await Orders.GetProductionPage(token,pages.actual,filters);
                if(!alive) return;
                if(!response || !response.success) throw new Error('load');
                const total = Math.max(1,Math.ceil(response.total/response.pageSize));
                if(pages.actual > total){setPages({total,actual:total});return;}
                setRecords(response.result);
                setPages(p => p.total === total ? p : {...p,total});
            } catch (_) {
                if(alive){setRecords([]);setError('No se pudo cargar la lista. Puedes intentar nuevamente.');}
            } finally {if(alive) isLoading(false);}
        },250);
        return () => {alive=false;clearTimeout(timer)};
    },[token,pages.actual,filters,retry]);
    const handleFilter = e => {
        setFilters(f => ({...f,[e.target.name]:e.target.value}));
        setPages(p => ({...p,actual:1}));
    };

    const handleDetail = async(order) => {
        handleChange(Detail, {order: order})
    }

    const handlePage = async(direction, end) => {
        const validation = pages.actual + direction;
        var selection = validation;

        if(validation > pages.total || validation < 1){
            return;
        }

        if(end){
            selection = direction > 0 ? pages.total : 1;
        }

        setPages({...pages, actual: selection})
    }

    const getCsvReport = async(dates, statuses, clients, callback) => {
        const params = await handleParameters(dates, statuses, clients);

        const response = await Orders.GetProductionCsvReport(token, params.dates, params.statuses, params.clients);

        if(!response.ok){
            const result = await response.json();
            await callback(result);
            return
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob); // Create a downloadable URL
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${Date.now()}.csv`; // Suggested file name
        document.body.appendChild(a);
        a.click();
        a.remove(); 
        await callback({success: true});
    }
        
    const handleParameters = async(dates, statuses, clients) => {
        const datesValues = [dates.from, dates.to];
        const clientsValue = Object.keys(clients).map(x => {
            if(clients[x]){
                return x;
            }
        });
        const statusesValue = Object.keys(statuses).map(x => {
            if(statuses[x]){
                return parseInt(x);
            }else{
                return 0;
            }
        });

        return {
            dates : datesValues,
            statuses: statusesValue,
            clients: clientsValue,
        }
    }

    const handleQuickSearch = async() => {
        const searchValue = quickId.trim();

        if(searchValue.length < 1){
            setQuickMatches([]);
            setQuickNotice({severity:'warning',message:'Ingresa un Order ID, Case ID o Reference ID.'});
            return;
        }

        setQuickSearching(true);
        setQuickMatches([]);
        setQuickNotice(null);

        try{
            const requests = [
                Orders.GetBySerial(searchValue.toUpperCase()),
                Orders.GetByReference(searchValue)
            ];
            if(/^\d+$/.test(searchValue) && Number(searchValue) > 0){
                requests.unshift(Orders.GetDetailFull(searchValue));
            }

            const responses = await Promise.all(requests);
            const sources = /^\d+$/.test(searchValue) && Number(searchValue) > 0
                ? ['ORDER ID','CASE ID','REFERENCE ID']
                : ['CASE ID','REFERENCE ID'];
            const matches = [];

            responses.forEach((response,index) => {
                const result = response?.result;
                if(!response?.success || !result || !result.id) return;
                const existing = matches.find(match => Number(match.id) === Number(result.id));
                if(existing){
                    if(!existing.sources.includes(sources[index])) existing.sources.push(sources[index]);
                    return;
                }
                matches.push({
                    id: result.id,
                    caseSerial: result.case_serial,
                    referenceId: result.reference_id,
                    sources: [sources[index]]
                });
            });

            if(matches.length < 1){
                setQuickNotice({severity:'error',message:'No se encontró en la base de datos.'});
                return;
            }

            if(matches.length === 1){
                setQuickId('');
                handleDetail(matches[0].id);
                return;
            }

            setQuickMatches(matches);
            setQuickNotice({severity:'warning',message:'Se encontró más de una coincidencia. Selecciona la orden correcta.'});
        }catch(e){
            setQuickNotice({severity:'error',message:'No se pudo completar la búsqueda. Intenta nuevamente.'});
        }finally{
            setQuickSearching(false);
        }
    }

    return(
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width:'100%',
            }}
        >
            <Csv 
                open={csvUi}
                onClose={() => showCsvUi(false)}
                onConfirm={getCsvReport}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography variant='h5' display={'block-inline'}>
                    Registered Orders
                </Typography>
                
                <Box
                    component='form'
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleQuickSearch();
                    }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap:2
                    }}
                >
                    <Typography variant='h5' display={'block-inline'}>
                        Quick search:
                    </Typography>
                    
                    <TextField 
                        placeholder='Insert Order ID, Case ID or Reference ID'
                        value={quickId}
                        onChange={e => {
                            setQuickId(e.target.value);
                            setQuickMatches([]);
                            setQuickNotice(null);
                        }}
                    />

                    <Button type='submit' variant='contained' endIcon={<SearchIcon/>} disabled={quickSearching}>{quickSearching?'Searching…':'Search'}</Button>
                </Box>

                <Button variant='contained' color='success' onClick={() => showCsvUi(true)}>CSV</Button>

            </Box>

            {quickNotice && <Alert severity={quickNotice.severity} sx={{mt:2}} onClose={() => setQuickNotice(null)}>{quickNotice.message}</Alert>}
            {quickMatches.length > 1 &&
                <Box sx={{display:'flex',flexDirection:'column',gap:1,mt:1}}>
                    {quickMatches.map(match =>
                        <Button
                            key={match.id}
                            variant='outlined'
                            onClick={() => {
                                setQuickId('');
                                setQuickMatches([]);
                                setQuickNotice(null);
                                handleDetail(match.id);
                            }}
                        >
                            {`${match.sources.join(' / ')} — Order ${match.id} — Case ${match.caseSerial || 'N/A'} — Reference ${match.referenceId || 'N/A'}`}
                        </Button>
                    )}
                </Box>
            }
            
            <Box
                sx={{
                    marginTop:2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
            >
                <Divider sx={{minWidth:'100%'}}></Divider>

                {error && <Box role="alert">{error} <Button onClick={() => setRetry(r => r+1)}>Reintentar</Button></Box>}
                {!loading && !error && records.length === 0 && <Typography role="status">No hay órdenes con estos filtros.</Typography>}
                <TableContainer sx={{mt:2, mb:8}}>
                    <Table
                        size='small'
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Order Date
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='date'
                                        onChange={handleFilter}
                                        value={filters.date}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Order ID
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='id'
                                        onChange={handleFilter}
                                        value={filters.id}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Order Client
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='client'
                                        onChange={handleFilter}
                                        value={filters.client}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Order Type
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='type'
                                        onChange={handleFilter}
                                        value={filters.type}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Reference ID
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='reference'
                                        onChange={handleFilter}
                                        value={filters.reference}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Tag ID
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='tag'
                                        onChange={handleFilter}
                                        value={filters.tag}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Status
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='status'
                                        onChange={handleFilter}
                                        value={filters.status}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={{...STYLES.Table001.HeaderSearch}}
                                >
                                    Parts
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Parts Min Status
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='minStatus'
                                        onChange={handleFilter}
                                        value={filters.minStatus}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Parts Top Status
                                    <br/>
                                    <TextField 
                                        size='small' 
                                        placeholder='Search' 
                                        sx={STYLES.Table001.HeaderSearchField}
                                        name='maxStatus'
                                        onChange={handleFilter}
                                        value={filters.maxStatus}
                                    />
                                </TableCell>
                                <TableCell 
                                    align='center'
                                    sx={STYLES.Table001.HeaderSearch}
                                >
                                    Actions
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {!filtering && display.length > 0 && display.map((row) => (
                            <TableRow
                                key={row.id}
                                sx={{
                                    backgroundColor: row.hasError ? 'lightcoral' : ''
                                }}
                            >
                                <TableCell align='center'>
                                    {row.date_create}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.id}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.client}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.type}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.reference_id}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.case_serial}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.status}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.part_count}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.minStatus}
                                </TableCell>
                                <TableCell align='center'>
                                    {row.maxStatus}
                                </TableCell>
                                <TableCell align='center'>
                                    <IconButton 
                                        aria-label="detail"
                                        onClick={() => handleDetail(row.id)}
                                    >
                                        <TextSnippetIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                    
                    {!filtering && !loading && records.length > 0 && (
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: '2%',
                                right: '40%',
                                marginTop: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <IconButton 
                                aria-label="detail"
                                sx={STYLES.Buttons.SimpleIcon}
                                onClick={() => handlePage(-1, true)}
                            >
                                <KeyboardDoubleArrowLeftIcon 
                                    fontSize='small'
                                />
                            </IconButton>
                            <IconButton 
                                aria-label="detail"
                                sx={STYLES.Buttons.SimpleIcon}
                                onClick={() => handlePage(-1, false)}
                            >
                                <KeyboardArrowLeftIcon 
                                    fontSize='large'
                                />
                            </IconButton>

                            <Typography variant='h5'>
                                {`${pages.actual}/${pages.total}`}
                            </Typography>

                            <IconButton 
                                aria-label="detail"
                                sx={STYLES.Buttons.SimpleIcon}
                                onClick={() => handlePage(1, false)}
                            >
                                <KeyboardArrowRightIcon 
                                    fontSize='large'
                                />
                            </IconButton>
                            <IconButton 
                                aria-label="detail"
                                sx={STYLES.Buttons.SimpleIcon}
                                onClick={() => handlePage(1, true)}
                            >
                                <KeyboardDoubleArrowRightIcon
                                    fontSize='small'
                                />
                            </IconButton>
                        </Box>
                    )}
                </TableContainer>
                {(loading) && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: '40%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography variant='h5'>
                            Loading Records from Page: {`${pages.actual}/${pages.total}`}
                        </Typography>
                        <CircularProgress />
                    </Box>
                )}

                {(filtering) && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: '40%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography variant='h5'>
                            Applying filters
                        </Typography>
                        <CircularProgress />
                    </Box>
                )}

                {(!filtering && !loading && display.length < 1 && records.length > 0) && (
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: '40%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <Typography variant='h5'>
                            No records found for search. Try next page.
                        </Typography>
                    </Box>
                )}

            </Box>
        </Box>
    );
}
